import { useEffect, useState } from "react";
import { FileText, Download, Eye, Users, Calendar, Tag, BookOpen, Shield } from "lucide-react";
import { AppLayout } from "@/components/layout/AppLayout";
import { Badge, Card, PrimaryButton, SectionTitle } from "@/components/Card";
import { useDemoStore } from "@/store/useDemoStore";

type Material = {
  id: string;
  titulo: string;
  descripcion: string;
  tipo: "pdf" | "video" | "enlace" | "presentacion";
  categoria: string;
  paginas?: number;
  duracionMin?: number;
  publicadoEn: string;
  dirigidoA: string[];
  tags: string[];
  icon: React.ReactNode;
};

const MATERIALES: Material[] = [
  {
    id: "mat-01",
    titulo: "Manual de técnica de conducción en circuito",
    descripcion:
      "Guía práctica para el candidato que cubre las 12 maniobras de desempeño evaluadas en EC1781: postura corporal, frenado progresivo, trazado de curvas, manejo de aceleración en rectas, slalom y técnica de vuelta rápida. Incluye diagramas de circuito y tablas de criterios de evaluación.",
    tipo: "pdf",
    categoria: "Técnica de conducción",
    paginas: 34,
    publicadoEn: "2025-11-10",
    dirigidoA: ["Grupo MTB-2025A", "Grupo MTB-2025B"],
    tags: ["D1–D12", "Desempeño", "Circuito", "EC1781"],
    icon: <BookOpen size={20} />,
  },
  {
    id: "mat-02",
    titulo: "Protocolo de seguridad en pista — NOM-206 y EPP",
    descripcion:
      "Documento de referencia obligatoria que describe el uso correcto del Equipo de Protección Personal (casco, guantes, traje, botas), las velocidades máximas permitidas por zona del circuito y el procedimiento de respuesta ante emergencia (criterio EM). Referencia directa a los criterios de actitud A1 y A2.",
    tipo: "pdf",
    categoria: "Seguridad",
    paginas: 18,
    publicadoEn: "2025-11-10",
    dirigidoA: ["Grupo MTB-2025A", "Grupo MTB-2025B"],
    tags: ["A1", "A2", "EM", "Seguridad", "NOM-206"],
    icon: <Shield size={20} />,
  },
];

const tipoTone: Record<string, "teal" | "navy" | "amber" | "green" | "gray"> = {
  pdf: "navy",
  video: "teal",
  presentacion: "amber",
  enlace: "gray",
};

const tipoLabel: Record<string, string> = {
  pdf: "PDF",
  video: "Video",
  presentacion: "Presentación",
  enlace: "Enlace",
};

function MaterialCard({ mat }: { mat: Material }) {
  const [descOpen, setDescOpen] = useState(false);

  return (
    <Card style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <div style={{ display: "flex", alignItems: "flex-start", gap: 14 }}>
        <div style={{
          width: 44, height: 44, borderRadius: 10, flexShrink: 0,
          background: "var(--cc-teal-bg)", color: "var(--cc-teal)",
          display: "flex", alignItems: "center", justifyContent: "center",
        }}>
          {mat.icon}
        </div>

        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 4 }}>
            <Badge tone={tipoTone[mat.tipo]}>{tipoLabel[mat.tipo]}</Badge>
            <span style={{ fontSize: 11, color: "var(--cc-gray-400)" }}>{mat.categoria}</span>
          </div>
          <div style={{ fontSize: 15, fontWeight: 700, color: "var(--cc-navy)", marginBottom: 4 }}>{mat.titulo}</div>

          <p style={{
            fontSize: 13, color: "var(--cc-gray-600)", margin: 0, lineHeight: 1.5,
            display: "-webkit-box", WebkitLineClamp: descOpen ? "unset" : 2,
            WebkitBoxOrient: "vertical", overflow: descOpen ? "visible" : "hidden",
          }}>
            {mat.descripcion}
          </p>
          {mat.descripcion.length > 120 && (
            <button
              onClick={() => setDescOpen((v) => !v)}
              style={{ fontSize: 12, color: "var(--cc-teal)", background: "none", border: "none", cursor: "pointer", padding: "2px 0", marginTop: 2 }}
            >
              {descOpen ? "Ver menos" : "Ver más"}
            </button>
          )}
        </div>
      </div>

      <div style={{ display: "flex", gap: 16, flexWrap: "wrap", fontSize: 12, color: "var(--cc-gray-500)" }}>
        {mat.paginas && (
          <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
            <FileText size={12} /> {mat.paginas} páginas
          </span>
        )}
        {mat.duracionMin && (
          <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
            <Eye size={12} /> {mat.duracionMin} min
          </span>
        )}
        <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
          <Calendar size={12} /> Publicado {mat.publicadoEn}
        </span>
        <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
          <Users size={12} /> {mat.dirigidoA.join(", ")}
        </span>
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
        <Tag size={11} style={{ color: "var(--cc-gray-400)" }} />
        {mat.tags.map((t) => (
          <span key={t} style={{
            fontSize: 10, fontWeight: 700, padding: "2px 6px", borderRadius: 4,
            background: "var(--cc-gray-100)", color: "var(--cc-gray-600)",
            letterSpacing: "0.03em",
          }}>{t}</span>
        ))}
      </div>

      <div style={{ display: "flex", gap: 8, paddingTop: 4, borderTop: "1px solid var(--cc-gray-100)" }}>
        <PrimaryButton variant="ghost" onClick={() => {}}>
          <Eye size={13} /> Vista previa
        </PrimaryButton>
        <PrimaryButton variant="ghost" onClick={() => {}}>
          <Download size={13} /> Descargar
        </PrimaryButton>
      </div>
    </Card>
  );
}

export default function InstructorMateriales() {
  const setRole = useDemoStore((s) => s.setRole);
  useEffect(() => setRole("instructor"), [setRole]);

  return (
    <AppLayout pageTitle="Materiales" breadcrumb={["Instructor", "Materiales"]}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16, flexWrap: "wrap", gap: 10 }}>
        <div>
          <SectionTitle style={{ margin: 0 }}>Materiales de apoyo</SectionTitle>
          <p style={{ fontSize: 13, color: "var(--cc-gray-500)", margin: "4px 0 0" }}>
            Documentos y recursos que el instructor comparte con los candidatos del grupo.
          </p>
        </div>
        <PrimaryButton onClick={() => {}}>
          + Agregar material
        </PrimaryButton>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        {MATERIALES.map((mat) => (
          <MaterialCard key={mat.id} mat={mat} />
        ))}
      </div>
    </AppLayout>
  );
}
